import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { EditorState } from '@codemirror/state';
import { Command, EditorView, ViewUpdate } from '@codemirror/view';
import { CodeLang } from '@common/codemirror/types/code-lang.type';
import { CmEditorComponent } from '@portal-core/code-editor/components/cm-editor/cm-editor.component';
import { CodeEditorToolbarComponent } from '@portal-core/code-editor/components/code-editor-toolbar/code-editor-toolbar.component';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { EditorToolbarDropdownMenuClosedEvent } from '@portal-core/ui/editor/types/editor-toolbar-dropdown-menu-closed-event.type';

@Component({
  selector: 'mc-code-editor',
  templateUrl: './code-editor.component.html',
  styleUrls: ['./code-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CodeEditorComponent {
  @Input() content: string;
  @Input() lang?: CodeLang;
  @Input() readonly?: boolean;
  @Input() showLang?: boolean = false;
  @Input() statusBarColor?: string;
  @Input() statusBarText?: string;
  @Input() wrapLines?: boolean;
  @Input() projectFileType?: ProjectFileType;

  @Output() contentChange: EventEmitter<ViewUpdate> = new EventEmitter<ViewUpdate>();
  @Output() wrapLinesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(CmEditorComponent, { static: true }) cmEditor: CmEditorComponent;
  @ViewChild(CodeEditorToolbarComponent, { static: false }) codeEditorToolbar: CodeEditorToolbarComponent;

  get editorState(): EditorState {
    return this.cmEditor?.editorState;
  }

  get editorView(): EditorView {
    return this.cmEditor?.editorView;
  }

  get lineCount(): number {
    return this.editorState?.doc.lines;
  }

  onDispatch(command: Command) {
    // Return focus to the editor before running a command from the toolbar
    this.editorView.focus();
    command(this.editorView);
  }

  onEditorStateChanged() {
    this.codeEditorToolbar?.updateState(this.editorView);
  }

  onToggleWrapLines() {
    this.wrapLines = !this.wrapLines;
  }

  onToolbarDropdownMenuClosed(event: EditorToolbarDropdownMenuClosedEvent<Command>) {
    // Only give focus to the editor if an item wasn't clicked and the menu wasn't closed because the user tabbed away
    if (!event.item && event.source !== 'tab') {
      // A toolbar dropdown was closed so give focus back to the editor
      this.editorView.focus();
    }
  }

  onWrapLinesChanged(wrapLines: boolean) {
    this.wrapLinesChange.emit(wrapLines);
  }

  public getContent(): string {
    return this.editorState?.doc?.toString();
  }

  public reset() {
    this.cmEditor?.reset();
  }
}
